import * as cookieUtils from "@/common/utils/cookieUtils";

const FavoritesCookieName = "_pxf";
const DefaultMaxFavoriteEntries = 6;
// 60 days in seconds - time to set for cookie expiration
const FavoritesTimeToLive = 5184000;
// Browsers drop a cookie write over 4096 bytes; keep the value under this.
const MaxCookieValueLength = 4000;

/*
 * We store the top maxEntries of favorites data
 * An entry looks like
 * [{
 *   "idexperience":"10001",
 *   "favorites": ["10004", "10005", "10006"],
 *   "lastUpdated": 1242351451
 * }]
 */
type Id = string | number;

/** One saved list: experience id, favorite article ids, last update time. */
interface FavoritesItem {
  id: Id;
  f: Id[];
  t?: number;
}

// Saved items by experience id, then by cluster name.
type SavedFavorites = Record<string, Record<string, FavoritesItem>>;

/** Serializes a copy without the oldest favorites of each list until it fits the cookie; memory keeps them. */
function trimOldestFavorites(saved: SavedFavorites): string {
  const copy = structuredClone(saved);
  const items = Object.values(copy).flatMap(entry => Object.values(entry));
  let value = JSON.stringify(copy);
  while (value.length > MaxCookieValueLength && items.length > 0) {
    const longest = items.reduce((a, b) => (b.f.length > a.f.length ? b : a));
    if (longest.f.length === 0) {
      break;
    }
    longest.f.shift();
    value = JSON.stringify(copy);
  }
  return value;
}

export default class FavoritesStorage {
  declare _maxEntries: number;
  declare _savedFavorites: SavedFavorites;

  constructor(max?: number) {
    this._maxEntries =
      typeof max === "undefined" ? DefaultMaxFavoriteEntries : max;
    this._savedFavorites = cookieUtils.getObject(
      FavoritesCookieName,
    ) as SavedFavorites;
    if (!this._savedFavorites) {
      this._savedFavorites = {};
    }
  }

  save() {
    let value = JSON.stringify(this._savedFavorites);
    while (value.length > MaxCookieValueLength && this._removeOldest(2)) {
      value = JSON.stringify(this._savedFavorites);
    }
    if (value.length > MaxCookieValueLength) {
      value = trimOldestFavorites(this._savedFavorites);
    }
    cookieUtils.set(FavoritesCookieName, value, {
      expires: FavoritesTimeToLive,
    });
  }

  getItem(idexperience: Id, cluster?: string): FavoritesItem | undefined {
    let entry: Record<string, FavoritesItem> | FavoritesItem | undefined =
      this._savedFavorites[idexperience];
    if (!entry) {
      return entry;
    }

    if (cluster) {
      entry = (entry as Record<string, FavoritesItem>)[cluster];
    }

    // Without a cluster this is the experience's whole entry; callers always pass one.
    return entry as FavoritesItem | undefined;
  }

  addItem(idexperience: Id, cluster: string, item: FavoritesItem) {
    let entry = this._savedFavorites[idexperience];
    let prune = false;
    if (!entry) {
      prune = true;
      entry = {};
    }

    if (!entry[cluster]) {
      prune = true;
    }

    if (prune) {
      this._pruneFavorites();
    }

    entry[cluster] = item;
    this._savedFavorites[idexperience] = entry;
  }

  getFavorites(idexperience: Id, cluster: string): Id[] | undefined {
    const item = this.getItem(idexperience, cluster);
    if (item) {
      return item.f;
    }
  }

  addFavorite(idexperience: Id, cluster: string, favorite: Id) {
    let item = this.getItem(idexperience, cluster);

    if (item) {
      item.f.push(favorite);
      item.t = this._currentTimestamp();
    } else {
      item = {
        id: idexperience,
        f: [favorite],
        t: this._currentTimestamp(),
      };
    }

    this.addItem(idexperience, cluster, item);
  }

  addFavorites(idexperience: Id, cluster: string, favorites: Id[]) {
    let item = this.getItem(idexperience, cluster);
    const t = this._currentTimestamp();

    if (!item) {
      item = {
        id: idexperience,
        f: [],
        t,
      };
    }

    for (let n = 0, max = favorites.length; n < max; n++) {
      item.f.push(favorites[n]);
    }
    item.t = t;
    this.addItem(idexperience, cluster, item);
  }

  _checkItem(idexperience: Id, cluster: string, item: FavoritesItem) {
    let ret = false;
    if (item.f.length === 0) {
      ret = true;
      const entry = this._savedFavorites[idexperience];
      delete entry[cluster];
      if (Object.keys(entry).length === 0) {
        delete this._savedFavorites[idexperience];
      }
    }

    return ret;
  }

  removeFavorite(idexperience: Id, cluster: string, favorite: Id) {
    const item = this.getItem(idexperience, cluster);
    let ret = false;
    if (item) {
      const index = item.f.indexOf(favorite);
      if (index > -1) {
        item.f.splice(index, 1);
        ret = true;
      }

      ret = this._checkItem(idexperience, cluster, item) || ret;
    }

    return ret;
  }

  removeFavorites(idexperience: Id, cluster: string, favorites: Id[]) {
    const item = this.getItem(idexperience, cluster);
    let ret = false;
    if (item) {
      for (let n = 0, max = favorites.length; n < max; n++) {
        const index = item.f.indexOf(favorites[n]);
        if (index > -1) {
          item.f.splice(index, 1);
          ret = true;
        }
      }
      ret = this._checkItem(idexperience, cluster, item) || ret;
    }

    return ret;
  }

  clearFavorites(idexperience: Id, cluster: string) {
    const item = this.getItem(idexperience, cluster);
    let ret = false;
    if (item) {
      ret = true;
      const entry = this._savedFavorites[idexperience];
      delete entry[cluster];
      if (Object.keys(entry).length === 0) {
        delete this._savedFavorites[idexperience];
      }
    }

    return ret;
  }

  clearAllFavorites() {
    this._savedFavorites = {};
    this.save();
  }

  removeFavoriteAtIndex(idexperience: Id, cluster: string, index: number) {
    const item = this.getItem(idexperience, cluster);
    let ret = false;
    if (item) {
      if (index < item.f.length && index >= 0) {
        item.f.splice(index, 1);
        ret = true;
      }
      ret = this._checkItem(idexperience, cluster, item) || ret;
    }

    return ret;
  }

  _pruneFavorites() {
    this._removeOldest(this._maxEntries);
  }

  /** Removes the oldest list when at least `minLists` lists are stored; returns whether it did. */
  _removeOldest(minLists: number) {
    let oldest: { id: string; cluster: string; t: number } | null = null;
    let count = 0;

    const keys = Object.keys(this._savedFavorites);
    for (let n = 0, maxn = keys.length; n < maxn; n++) {
      const entry = this._savedFavorites[keys[n]];
      const ikeys = Object.keys(entry);
      for (let m = 0, maxm = ikeys.length; m < maxm; m++) {
        count++;
        const t = Number(entry[ikeys[m]].t);
        if (oldest == null || t < oldest.t) {
          oldest = { id: keys[n], cluster: ikeys[m], t };
        }
      }
    }

    if (!oldest || count < minLists) {
      return false;
    }
    const entry = this._savedFavorites[oldest.id];
    delete entry[oldest.cluster];
    if (Object.keys(entry).length === 0) {
      delete this._savedFavorites[oldest.id];
    }
    return true;
  }

  _currentTimestamp() {
    return Date.now();
  }
}
